import { createContext, useContext } from "react";
import type { ReactNode } from "react";

/**
 * Transforms (values, options) before the inner SDK submit.
 *
 * Use it to inject cross-cutting concerns — dev-panel scenario replay, e2e
 * test scaffolding, request decoration — without coupling application code
 * to those concerns. Application code keeps calling `submit(input)`; the
 * middleware adds the rest.
 *
 * Synchronous and pure: receives the current values+options, returns the
 * next pair. Middlewares run in registration order; each sees the result
 * of the previous one. Values is `unknown` because the package is generic
 * over StateType — middleware authors who care about typed access can
 * narrow internally.
 */
export type SubmitMiddleware = (
  values: unknown,
  options: Record<string, unknown> | undefined
) => { values: unknown; options: Record<string, unknown> | undefined };

const SubmitMiddlewareContext = createContext<readonly SubmitMiddleware[]>([]);

/**
 * Registers submit middlewares for every `useLanggraphStream` descendant.
 * Wrap once near the app root (or scope tighter for per-feature isolation).
 *
 *   <SubmitMiddlewareProvider middlewares={[devReplayMiddleware]}>
 *     <App />
 *   </SubmitMiddlewareProvider>
 *
 * Middlewares execute in array order on every submit.
 */
export function SubmitMiddlewareProvider({
  middlewares,
  children,
}: {
  middlewares: readonly SubmitMiddleware[];
  children: ReactNode;
}) {
  return (
    <SubmitMiddlewareContext.Provider value={middlewares}>
      {children}
    </SubmitMiddlewareContext.Provider>
  );
}

/**
 * Reads the current middleware chain from context. Returns an empty array
 * outside a provider — submits pass through unchanged, matching pre-existing
 * behavior.
 */
export function useSubmitMiddlewares(): readonly SubmitMiddleware[] {
  return useContext(SubmitMiddlewareContext);
}
